import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  I18nManager,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import twoFactorService from '../services/twoFactorService';
import logger from '../utils/logger';
import { platformStorage } from '../utils/storage';

type Pending2FAPayload = {
  tempToken: string;
  email: string;
  has2FA: { totpEnabled: boolean; emailOtpEnabled: boolean };
  createdAt: number;
};

interface TwoFactorVerifyScreenProps {
  navigation: any;
  route: {
    params?: {};
  };
}

export default function TwoFactorVerifyScreen({ navigation, route }: TwoFactorVerifyScreenProps) {
  const { t } = useTranslation();
  const { theme, isDark } = useTheme();
  const { verify2FA, setPending2FA } = useAuth();
  const isRTL = I18nManager.isRTL;

  // ✅ State لحفظ بيانات 2FA من Storage
  const [session, setSession] = useState<Pending2FAPayload | null>(null);
  const [booting, setBooting] = useState(true);

  const [code, setCode] = useState('');
  const [method, setMethod] = useState<'totp' | 'email_otp' | 'backup_code'>('totp');
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [maxAttempts] = useState(3);
  const codeInputRef = React.useRef<any>(null);

  // ✅ Auto-focus on code input after session loads
  useEffect(() => {
    if (session && codeInputRef.current) {
      setTimeout(() => {
        codeInputRef.current?.focus();
      }, 500);
    }
  }, [session]);

  // ✅ قراءة بيانات 2FA من Storage بدل params
  useEffect(() => {
    (async () => {
      try {
        const raw = await platformStorage.getItem('pending_2fa');
        if (!raw) {
          logger.warn('[2FA] No pending_2fa payload found');
          setSession(null);
          setBooting(false);
          return;
        }

        const parsed: Pending2FAPayload = JSON.parse(raw);

        // ✅ انتهاء الصلاحية بعد 10 دقائق - auto-redirect to login
        const ageMs = Date.now() - (parsed.createdAt || 0);
        if (parsed.createdAt && ageMs > 10 * 60 * 1000) {
          logger.warn('[2FA] pending_2fa expired, auto-redirecting to login...');
          await platformStorage.removeItem('pending_2fa');
          setPending2FA(false);
          setBooting(false);
          navigation.reset({ index: 0, routes: [{ name: 'Login' }] });
          return;
        }

        setSession(parsed);
        setMethod(parsed.has2FA?.totpEnabled ? 'totp' : 'email_otp');

        logger.log('[2FA] Loaded pending_2fa payload', {
          hasEmail: !!parsed.email,
          hasTempToken: !!parsed.tempToken,
          has2FA: parsed.has2FA,
        });
      } catch (e) {
        logger.error('[2FA] Failed to load pending_2fa:', e);
        setSession(null);
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  // ✅ استخراج البيانات من session بدل params
  const userEmail = session?.email || '';
  const tempToken = session?.tempToken;

  // ✅ CRITICAL: Log raw has2FA to debug structure
  useEffect(() => {
    if (session?.has2FA) {
      logger.log(
        '🔍 [TwoFactorVerify] RAW has2FA from session:',
        JSON.stringify(session.has2FA, null, 2),
      );
    }
  }, [session?.has2FA]);

  // ✅ Normalize has2FA structure (backend may return different formats)
  const has2FA = useMemo(() => {
    const raw = session?.has2FA as any; // Support multiple backend formats
    if (!raw) return { totpEnabled: false, emailOtpEnabled: false };

    // Support all backend formats:
    // { totp: true, email: true }  ← Backend actual format
    // { totpEnabled: true, emailOtpEnabled: true }
    // { totp: true, emailOtp: true } OR { totp: true, email_otp: true }
    return {
      totpEnabled: !!(raw.totpEnabled || raw.totp),
      emailOtpEnabled: !!(raw.emailOtpEnabled || raw.emailOtp || raw.email_otp || raw.email),
    };
  }, [session?.has2FA]);

  // ✅ Auto-select first enabled method (prevents "method not enabled" error)
  useEffect(() => {
    logger.log('🔍 [TwoFactorVerify] Normalized has2FA:', has2FA);

    if (has2FA.totpEnabled) {
      setMethod('totp');
      logger.log('[TwoFactorVerify] ✅ Default method: TOTP');
    } else if (has2FA.emailOtpEnabled) {
      setMethod('email_otp');
      logger.log('[TwoFactorVerify] ✅ Default method: Email OTP');
    } else {
      // No 2FA enabled - fallback to backup code
      setMethod('backup_code');
      logger.log('[TwoFactorVerify] ⚠️ No 2FA enabled, defaulting to backup_code');
    }
  }, [has2FA]);

  const handleSendEmailOtp = async () => {
    // ✅ Validate emailOtpEnabled first
    if (!has2FA?.emailOtpEnabled) {
      logger.error('[handleSendEmailOtp] ❌ Email OTP not enabled for user');
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        isRTL ? 'رمز البريد غير مفعّل لهذا المستخدم' : 'Email OTP is not enabled for this user',
      );
      return;
    }

    // ✅ Validate tempToken exists
    if (!tempToken) {
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        isRTL
          ? 'لا توجد جلسة صالحة. الرجاء تسجيل الدخول مرة أخرى.'
          : 'No valid session. Please login again.',
      );
      return;
    }

    try {
      setSendingOtp(true);
      logger.log('[handleSendEmailOtp] 📧 Requesting OTP with tempToken');

      // ✅ Pass tempToken to sendEmailOtpForLogin
      await twoFactorService.sendEmailOtpForLogin(tempToken);
      setOtpSent(true);

      logger.log('[handleSendEmailOtp] ✅ OTP sent successfully');
      Alert.alert(
        isRTL ? 'تم الإرسال' : 'Sent',
        isRTL ? `تم إرسال رمز التحقق إلى ${userEmail}` : `Verification code sent to ${userEmail}`,
      );
    } catch (error: any) {
      logger.error('[handleSendEmailOtp] ❌ Failed:', error);
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        error.message || (isRTL ? 'فشل إرسال الرمز' : 'Failed to send code'),
      );
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerify = async () => {
    logger.log('🔵 [TwoFactorVerify] handleVerify called');
    logger.log('📝 Code:', code);
    logger.log('📝 Method:', method);
    logger.log('📝 Code length:', code.length);

    if (code.length < 4) {
      logger.log('❌ Code too short');
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        isRTL ? 'الرجاء إدخال رمز التحقق' : 'Please enter verification code',
      );
      return;
    }

    // Check if max attempts reached
    if (attempts >= maxAttempts) {
      logger.log('❌ Max attempts reached');
      Alert.alert(
        isRTL ? 'تم تجاوز الحد الأقصى' : 'Max Attempts Reached',
        isRTL
          ? `لقد تجاوزت الحد الأقصى للمحاولات (${maxAttempts}). الرجاء المحاولة لاحقاً.`
          : `You have exceeded the maximum number of attempts (${maxAttempts}). Please try again later.`,
      );
      return;
    }

    try {
      setLoading(true);

      logger.log('🚀 [TwoFactorVerify] Calling verify2FA with:');
      logger.log('  - Method:', method);
      logger.log('  - Code length:', code.length);
      logger.log('  - Has tempToken:', !!tempToken);
      logger.log('  - User has2FA status:', has2FA);
      logger.log('  - TOTP enabled:', has2FA?.totpEnabled);
      logger.log('  - Email OTP enabled:', has2FA?.emailOtpEnabled);

      // ✅ Validate selected method is enabled
      if (method === 'totp' && !has2FA?.totpEnabled) {
        throw new Error(
          isRTL ? 'تطبيق المصادقة غير مفعّل لهذا المستخدم' : 'TOTP is not enabled for this user',
        );
      }
      if (method === 'email_otp' && !has2FA?.emailOtpEnabled) {
        throw new Error(
          isRTL ? 'رمز البريد غير مفعّل لهذا المستخدم' : 'Email OTP is not enabled for this user',
        );
      }

      // Ensure tempToken exists
      if (!tempToken) {
        throw new Error(
          isRTL ? 'رمز المصادقة المؤقت مفقود' : 'Temporary authentication token is missing',
        );
      }

      // ✅ استخدام verify2FA من AuthContext
      await verify2FA(tempToken, code, method);

      // ✅ مسح بيانات 2FA من Storage
      await platformStorage.removeItem('pending_2fa');
      setPending2FA(false);

      logger.log('✅ 2FA Verification SUCCESS!');
      logger.log('🔄 [TwoFactorVerify] Navigation will be handled by AuthContext.checkAuth()');

      // Reset attempts
      setAttempts(0);

      // ✅ Navigation handled automatically by AuthContext checkAuth()
      // After verify2FA sets tokens, checkAuth() will detect authentication
      // and RootNavigator will switch to authenticated Stack with Main screen
    } catch (error: any) {
      // Increment attempts
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      const remainingAttempts = maxAttempts - newAttempts;
      const attemptsMsg =
        remainingAttempts > 0
          ? isRTL
            ? `\nالمحاولات المتبقية: ${remainingAttempts}`
            : `\nAttempts remaining: ${remainingAttempts}`
          : '';

      logger.log('❌ Verification failed:', error.message);
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        (error.message || (isRTL ? 'رمز التحقق غير صحيح' : 'Invalid verification code')) +
          attemptsMsg,
      );
    } finally {
      setLoading(false);
    }
  };

  const getMethodTitle = () => {
    switch (method) {
      case 'totp':
        return isRTL ? 'تطبيق المصادقة' : 'Authenticator App';
      case 'email_otp':
        return isRTL ? 'البريد الإلكتروني' : 'Email';
      case 'backup_code':
        return isRTL ? 'رمز احتياطي' : 'Backup Code';
      default:
        return '';
    }
  };

  const getMethodDescription = () => {
    switch (method) {
      case 'totp':
        return isRTL
          ? 'أدخل الرمز المكون من 6 أرقام من تطبيق المصادقة'
          : 'Enter the 6-digit code from your authenticator app';
      case 'email_otp':
        return isRTL
          ? `سنرسل رمز التحقق إلى ${userEmail}`
          : `We'll send a verification code to ${userEmail}`;
      case 'backup_code':
        return isRTL
          ? 'أدخل أحد رموز الاحتياطي التي حفظتها'
          : 'Enter one of your saved backup codes';
      default:
        return '';
    }
  };

  // ✅ Loading state
  if (booting) {
    return (
      <View
        style={[
          styles.container,
          {
            backgroundColor: theme.colors.background,
            alignItems: 'center',
            justifyContent: 'center',
          },
        ]}
      >
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={{ marginTop: 12, color: theme.colors.textSecondary }}>
          {isRTL ? 'جاري التحضير...' : 'Preparing...'}
        </Text>
      </View>
    );
  }

  // ✅ Session not found
  if (!session) {
    return (
      <View
        style={[
          styles.container,
          {
            backgroundColor: theme.colors.background,
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          },
        ]}
      >
        <Ionicons name="alert-circle" size={42} color={theme.colors.error} />
        <Text
          style={{
            marginTop: 12,
            color: theme.colors.text,
            fontSize: 16,
            fontWeight: '700',
            textAlign: 'center',
          }}
        >
          {isRTL ? 'جلسة التحقق غير متاحة' : '2FA session not available'}
        </Text>
        <Text style={{ marginTop: 8, color: theme.colors.textSecondary, textAlign: 'center' }}>
          {isRTL ? 'ارجع لتسجيل الدخول وحاول مرة أخرى.' : 'Please go back to login and try again.'}
        </Text>

        <TouchableOpacity
          style={[styles.verifyButton, { backgroundColor: theme.colors.primary, marginTop: 16 }]}
          onPress={async () => {
            await platformStorage.removeItem('pending_2fa');
            setPending2FA(false);
            navigation.reset({ index: 0, routes: [{ name: 'Login' }] });
          }}
        >
          <Text style={styles.verifyButtonText}>
            {isRTL ? 'العودة لتسجيل الدخول' : 'Back to Login'}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={0}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={styles.header}>
          {/* ✅ زر الرجوع: مسح pending_2fa والعودة للوجن */}
          <TouchableOpacity
            onPress={async () => {
              await platformStorage.removeItem('pending_2fa');
              setPending2FA(false);
              navigation.reset({ index: 0, routes: [{ name: 'Login' }] });
            }}
            style={styles.backButton}
          >
            <Ionicons
              name={isRTL ? 'chevron-forward' : 'chevron-back'}
              size={28}
              color={theme.colors.text}
            />
          </TouchableOpacity>
          <Text style={[styles.title, { color: theme.colors.text }]}>
            {isRTL ? 'التحقق بخطوتين' : 'Two-Factor Verification'}
          </Text>
        </View>

        {/* Icon */}
        <View style={styles.iconContainer}>
          <View style={[styles.iconCircle, { backgroundColor: theme.colors.primary + '20' }]}>
            <Ionicons name="shield-checkmark" size={60} color={theme.colors.primary} />
          </View>
        </View>

        {/* Attempts Counter */}
        {attempts > 0 && (
          <View
            style={[
              styles.attemptsContainer,
              {
                backgroundColor: theme.colors.card,
                borderColor: attempts >= maxAttempts - 1 ? theme.colors.error : theme.colors.border,
              },
            ]}
          >
            <Ionicons
              name={attempts >= maxAttempts - 1 ? 'warning' : 'information-circle'}
              size={20}
              color={attempts >= maxAttempts - 1 ? theme.colors.error : theme.colors.textSecondary}
            />
            <Text
              style={[
                styles.attemptsText,
                {
                  color:
                    attempts >= maxAttempts - 1 ? theme.colors.error : theme.colors.textSecondary,
                },
              ]}
            >
              {isRTL
                ? `المحاولات: ${attempts}/${maxAttempts}`
                : `Attempts: ${attempts}/${maxAttempts}`}
            </Text>
          </View>
        )}

        {/* Method Selector */}
        <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
            {isRTL ? 'طريقة التحقق' : 'Verification Method'}
          </Text>

          <View style={styles.methodButtons}>
            {/* TOTP Method - Only show if enabled */}
            {has2FA.totpEnabled && (
              <TouchableOpacity
                style={[
                  styles.methodButton,
                  {
                    backgroundColor: theme.colors.background,
                    borderColor: method === 'totp' ? theme.colors.primary : theme.colors.border,
                    borderWidth: 2,
                  },
                ]}
                onPress={() => {
                  logger.log('[TwoFactorVerify] Method changed to: totp');
                  setMethod('totp');
                }}
              >
                <Ionicons
                  name="phone-portrait"
                  size={24}
                  color={method === 'totp' ? theme.colors.primary : theme.colors.textSecondary}
                />
                <Text
                  style={[
                    styles.methodButtonText,
                    {
                      color: method === 'totp' ? theme.colors.primary : theme.colors.textSecondary,
                    },
                  ]}
                >
                  {isRTL ? 'تطبيق المصادقة' : 'Authenticator'}
                </Text>
              </TouchableOpacity>
            )}

            {/* Email OTP Method - Only show if enabled */}
            {has2FA.emailOtpEnabled && (
              <TouchableOpacity
                style={[
                  styles.methodButton,
                  {
                    backgroundColor: theme.colors.background,
                    borderColor:
                      method === 'email_otp' ? theme.colors.primary : theme.colors.border,
                    borderWidth: 2,
                  },
                ]}
                onPress={() => {
                  logger.log('[TwoFactorVerify] Method changed to: email_otp');
                  setMethod('email_otp');
                }}
              >
                <Ionicons
                  name="mail"
                  size={24}
                  color={method === 'email_otp' ? theme.colors.primary : theme.colors.textSecondary}
                />
                <Text
                  style={[
                    styles.methodButtonText,
                    {
                      color:
                        method === 'email_otp' ? theme.colors.primary : theme.colors.textSecondary,
                    },
                  ]}
                >
                  {isRTL ? 'البريد' : 'Email'}
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[
                styles.methodButton,
                {
                  backgroundColor: theme.colors.background,
                  borderColor:
                    method === 'backup_code' ? theme.colors.primary : theme.colors.border,
                  borderWidth: 2,
                },
              ]}
              onPress={() => setMethod('backup_code')}
            >
              <Ionicons
                name="key"
                size={24}
                color={method === 'backup_code' ? theme.colors.primary : theme.colors.textSecondary}
              />
              <Text
                style={[
                  styles.methodButtonText,
                  {
                    color:
                      method === 'backup_code' ? theme.colors.primary : theme.colors.textSecondary,
                  },
                ]}
              >
                {isRTL ? 'احتياطي' : 'Backup'}
              </Text>
            </TouchableOpacity>
          </View>

          <Text style={[styles.methodDescription, { color: theme.colors.textSecondary }]}>
            {getMethodDescription()}
          </Text>

          {/* Send OTP Button (for email method) */}
          {method === 'email_otp' && !otpSent && (
            <TouchableOpacity
              style={[
                styles.sendOtpButton,
                { backgroundColor: theme.colors.primary },
                sendingOtp && styles.sendOtpButtonDisabled,
              ]}
              onPress={handleSendEmailOtp}
              disabled={sendingOtp}
            >
              {sendingOtp ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <>
                  <Ionicons name="mail-outline" size={20} color="#fff" />
                  <Text style={styles.sendOtpButtonText}>
                    {isRTL ? 'إرسال الرمز' : 'Send Code'}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          )}

          {/* Code Input */}
          <View style={styles.inputHeader}>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              {isRTL ? 'رمز التحقق' : 'Verification Code'}
            </Text>
            <TouchableOpacity
              style={styles.focusButton}
              onPress={() => codeInputRef.current?.focus()}
            >
              <Ionicons name="create-outline" size={18} color={theme.colors.primary} />
              <Text style={[styles.focusButtonText, { color: theme.colors.primary }]}>
                {isRTL ? 'إدخال' : 'Input'}
              </Text>
            </TouchableOpacity>
          </View>
          <TextInput
            ref={codeInputRef}
            style={[
              styles.codeInput,
              {
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                borderColor: theme.colors.border,
              },
            ]}
            value={code}
            onChangeText={(text) => {
              setCode(text);
              // ✅ Auto-dismiss keyboard and verify when 6 digits entered
              const expectedLength = method === 'backup_code' ? 9 : 6;
              if (text.length === expectedLength) {
                setTimeout(() => {
                  codeInputRef.current?.blur();
                }, 100);
              }
            }}
            placeholder={
              method === 'totp' ? '123456' : method === 'backup_code' ? 'XXXX-XXXX' : '123456'
            }
            placeholderTextColor={theme.colors.textSecondary}
            keyboardType={method === 'backup_code' ? 'default' : 'number-pad'}
            maxLength={method === 'backup_code' ? 9 : 6}
            textAlign="center"
            returnKeyType="done"
            blurOnSubmit={true}
            onSubmitEditing={() => {
              codeInputRef.current?.blur();
              if (code.length >= 4) {
                handleVerify();
              }
            }}
            autoFocus={true}
          />

          {/* Verify Button */}
          <TouchableOpacity
            style={[
              styles.verifyButton,
              { backgroundColor: theme.colors.primary },
              (loading || code.length < 4) && styles.verifyButtonDisabled,
            ]}
            onPress={handleVerify}
            disabled={loading || code.length < 4}
          >
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <>
                <Ionicons name="checkmark-circle" size={20} color="#fff" />
                <Text style={styles.verifyButtonText}>{isRTL ? 'تحقق' : 'Verify'}</Text>
              </>
            )}
          </TouchableOpacity>

          {/* Resend OTP (for email) */}
          {method === 'email_otp' && otpSent && (
            <TouchableOpacity
              style={styles.resendButton}
              onPress={handleSendEmailOtp}
              disabled={sendingOtp}
            >
              <Text style={[styles.resendButtonText, { color: theme.colors.primary }]}>
                {isRTL ? 'إعادة إرسال الرمز' : 'Resend Code'}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Help Text */}
        <Text style={[styles.helpText, { color: theme.colors.textSecondary }]}>
          {isRTL
            ? 'لا يمكنك الوصول إلى رمز التحقق؟ جرب رمز احتياطي أو اتصل بالدعم.'
            : "Can't access your verification code? Try a backup code or contact support."}
        </Text>
      </ScrollView>
    </KeyboardAvoidingView>
  );
} // ✅ Close TwoFactorVerifyScreen function

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    paddingTop: 60,
  },
  backButton: {
    marginRight: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  iconContainer: {
    alignItems: 'center',
    marginVertical: 32,
  },
  iconCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    margin: 16,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  methodButtons: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  methodButton: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    padding: 16,
    borderRadius: 8,
    gap: 8,
  },
  methodButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
  methodDescription: {
    fontSize: 13,
    lineHeight: 20,
  },
  sendOtpButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
    gap: 8,
  },
  sendOtpButtonDisabled: {
    opacity: 0.6,
  },
  sendOtpButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  codeInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 16,
    fontSize: 24,
    fontFamily: 'monospace',
    letterSpacing: 4,
  },
  verifyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 8,
    marginTop: 16,
    gap: 8,
  },
  verifyButtonDisabled: {
    opacity: 0.6,
  },
  verifyButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  resendButton: {
    alignItems: 'center',
    marginTop: 12,
    padding: 8,
  },
  resendButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  helpText: {
    fontSize: 13,
    textAlign: 'center',
    paddingHorizontal: 32,
    marginTop: 24,
    lineHeight: 20,
  },
  attemptsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    marginHorizontal: 24,
    marginBottom: 16,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  attemptsText: {
    fontSize: 14,
    fontWeight: '600',
  },
  inputHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 24,
    marginBottom: 8,
  },
  focusButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  focusButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },
});
